import type { ContactMongoFilter } from '@/modules/contacts/types/contacts.types';
import { parseContactGroupCustomFilter } from '@/modules/contacts/utils/custom-filter';

const AUDIENCE_META_PREFIX = 'mc:audience:';

export type CampaignAudienceSelection = {
  includeListIds: string[];
  includeSegmentIds: string[];
  excludeSegmentIds: string[];
  /** Optional so older selections (segments-only excludes) stay valid. */
  excludeListIds?: string[];
};

export const EMPTY_AUDIENCE_SELECTION: CampaignAudienceSelection = {
  includeListIds: [],
  includeSegmentIds: [],
  excludeSegmentIds: [],
};

export const AUDIENCE_SKIP_REASONS = [
  { code: 'unsubscribed', label: 'Unsubscribed' },
  { code: 'pending', label: 'Opt-in pending' },
  { code: 'suppressed', label: 'On the suppression list' },
  { code: 'invalid-email', label: 'Invalid address' },
  { code: 'duplicate', label: 'Duplicate in audience' },
] as const;

const EMAIL_CLAUSE: ContactMongoFilter = {
  Email: { $exists: true, $nin: [null, ''] },
};

/** Publish / sendable gate — email present + campaign-subscribed. */
export const CAMPAIGN_SENDABLE_CLAUSE: ContactMongoFilter = {
  ...EMAIL_CLAUSE,
  IsCampaignSubscribed: true,
};

export const parseCampaignAudienceMeta = (
  tags: string[] | null | undefined
): CampaignAudienceSelection => {
  const raw = (tags ?? []).find((tag) => tag.startsWith(AUDIENCE_META_PREFIX));
  if (!raw) return { ...EMPTY_AUDIENCE_SELECTION, includeListIds: [], includeSegmentIds: [], excludeSegmentIds: [] };

  try {
    const parsed = JSON.parse(raw.slice(AUDIENCE_META_PREFIX.length)) as Partial<CampaignAudienceSelection>;
    return {
      includeListIds: Array.isArray(parsed.includeListIds)
        ? parsed.includeListIds.filter((id): id is string => typeof id === 'string' && Boolean(id))
        : [],
      includeSegmentIds: Array.isArray(parsed.includeSegmentIds)
        ? parsed.includeSegmentIds.filter((id): id is string => typeof id === 'string' && Boolean(id))
        : [],
      excludeSegmentIds: Array.isArray(parsed.excludeSegmentIds)
        ? parsed.excludeSegmentIds.filter((id): id is string => typeof id === 'string' && Boolean(id))
        : [],
      excludeListIds: Array.isArray(parsed.excludeListIds)
        ? parsed.excludeListIds.filter((id): id is string => typeof id === 'string' && Boolean(id))
        : [],
    };
  } catch {
    return { ...EMPTY_AUDIENCE_SELECTION };
  }
};

export const upsertCampaignAudienceMeta = (
  tags: string[] | null | undefined,
  selection: CampaignAudienceSelection
): string[] => {
  const without = (tags ?? []).filter((tag) => !tag.startsWith(AUDIENCE_META_PREFIX));
  const payload: CampaignAudienceSelection = {
    includeListIds: [...selection.includeListIds],
    includeSegmentIds: [...selection.includeSegmentIds],
    excludeSegmentIds: [...selection.excludeSegmentIds],
    excludeListIds: [...(selection.excludeListIds ?? [])],
  };
  if (
    payload.includeListIds.length === 0 &&
    payload.includeSegmentIds.length === 0 &&
    payload.excludeSegmentIds.length === 0 &&
    (payload.excludeListIds?.length ?? 0) === 0
  ) {
    return without;
  }
  return [...without, `${AUDIENCE_META_PREFIX}${JSON.stringify(payload)}`];
};

export const isAudienceSelectionValid = (selection: CampaignAudienceSelection): boolean =>
  selection.includeListIds.length + selection.includeSegmentIds.length > 0;

export const getAudienceOverlapIds = (selection: CampaignAudienceSelection): string[] => [
  ...selection.includeSegmentIds.filter((id) => selection.excludeSegmentIds.includes(id)),
  ...selection.includeListIds.filter((id) => (selection.excludeListIds ?? []).includes(id)),
];

export const toggleAudienceId = (ids: string[], id: string): string[] => {
  if (ids.includes(id)) return ids.filter((item) => item !== id);
  return [...ids, id];
};

export const getResolvableAudienceSegmentIds = (
  selectedIds: string[],
  segments: { itemId: string }[]
): string[] => {
  const availableIds = new Set(segments.map((segment) => segment.itemId));
  return selectedIds.filter((id) => availableIds.has(id));
};

type SegmentFilterSource = {
  ItemId: string;
  CustomFilter?: string | null;
};

const NEVER_MATCH_CLAUSE: ContactMongoFilter = { ItemId: { $in: [] } };

const filtersForSegmentIds = (
  segmentIds: string[],
  segments: SegmentFilterSource[]
): ContactMongoFilter[] => {
  const byId = new Map(segments.map((segment) => [segment.ItemId, segment]));
  return segmentIds
    .map((id) => {
      const filter = parseContactGroupCustomFilter(byId.get(id)?.CustomFilter);
      return filter && Object.keys(filter).length > 0 ? filter : null;
    })
    .filter((filter): filter is ContactMongoFilter => Boolean(filter));
};

const hasUnresolvedIncludeSegments = (
  selection: CampaignAudienceSelection,
  segments: SegmentFilterSource[]
): boolean =>
  filtersForSegmentIds(selection.includeSegmentIds, segments).length !==
  selection.includeSegmentIds.length;

const buildInclusionClause = (
  selection: CampaignAudienceSelection,
  segments: SegmentFilterSource[]
): ContactMongoFilter | null => {
  if (hasUnresolvedIncludeSegments(selection, segments)) return NEVER_MATCH_CLAUSE;

  const clauses: ContactMongoFilter[] = [];

  if (selection.includeListIds.length > 0) {
    clauses.push({ ArcLeadCustomGroupEntityIds: { $in: selection.includeListIds } });
  }

  clauses.push(...filtersForSegmentIds(selection.includeSegmentIds, segments));

  if (clauses.length === 0) return null;
  if (clauses.length === 1) return clauses[0];
  return { $or: clauses };
};

/**
 * Membership filter: (lists ∪ include segments) − exclude segments.
 * Does not apply the sendable/subscription gate.
 */
export const buildAudienceMembershipWhere = (
  selection: CampaignAudienceSelection,
  segments: SegmentFilterSource[]
): ContactMongoFilter => {
  if (hasUnresolvedIncludeSegments(selection, segments)) return NEVER_MATCH_CLAUSE;

  const inclusion = buildInclusionClause(selection, segments);
  if (!inclusion) return { ...EMAIL_CLAUSE };

  const excludeFilters = filtersForSegmentIds(selection.excludeSegmentIds, segments);
  const parts: ContactMongoFilter[] = [{ ...EMAIL_CLAUSE }, inclusion];
  if (excludeFilters.length === 1) {
    parts.push({ $nor: [excludeFilters[0]] });
  } else if (excludeFilters.length > 1) {
    parts.push({ $nor: excludeFilters });
  }

  return parts.length === 1 ? parts[0] : { $and: parts };
};

/** Membership + subscribed — used for sendable estimate and publish query. */
export const buildAudienceSendableWhere = (
  selection: CampaignAudienceSelection,
  segments: SegmentFilterSource[]
): ContactMongoFilter => {
  if (hasUnresolvedIncludeSegments(selection, segments)) return NEVER_MATCH_CLAUSE;

  const membership = buildAudienceMembershipWhere(selection, segments);
  if ('$and' in membership && Array.isArray(membership.$and)) {
    return {
      $and: [...(membership.$and as ContactMongoFilter[]), { IsCampaignSubscribed: true }],
    };
  }
  return { $and: [membership, { IsCampaignSubscribed: true }] };
};

const toSingleQuoteMongoString = (value: unknown): string =>
  JSON.stringify(value).replaceAll('"', "'");

export const buildAudiencePublishGraphQLQuery = (
  selection: CampaignAudienceSelection,
  segments: SegmentFilterSource[]
): string | undefined => {
  if (!isAudienceSelectionValid(selection) || hasUnresolvedIncludeSegments(selection, segments)) {
    return undefined;
  }
  return toSingleQuoteMongoString(buildAudienceSendableWhere(selection, segments));
};

export const getDesignAudienceSummary = (
  selection: CampaignAudienceSelection,
  lists: { id: string; name: string }[],
  segments: { ItemId: string; Name?: string | null }[]
): string => {
  if (!isAudienceSelectionValid(selection)) {
    return 'No lists or segments selected';
  }

  const listNames = selection.includeListIds
    .map((id) => lists.find((list) => list.id === id)?.name?.trim() || 'Untitled list')
    .slice(0, 2);
  const segmentNames = selection.includeSegmentIds
    .map(
      (id) =>
        segments.find((segment) => segment.ItemId === id)?.Name?.trim() || 'Untitled segment'
    )
    .slice(0, 2);

  const parts: string[] = [];
  if (listNames.length) {
    parts.push(
      selection.includeListIds.length > 2
        ? `${listNames.join(', ')} +${selection.includeListIds.length - 2} lists`
        : listNames.join(', ')
    );
  }
  if (segmentNames.length) {
    parts.push(
      selection.includeSegmentIds.length > 2
        ? `${segmentNames.join(', ')} +${selection.includeSegmentIds.length - 2} segments`
        : segmentNames.join(', ')
    );
  }
  if (selection.excludeSegmentIds.length > 0) {
    parts.push(`excluding ${selection.excludeSegmentIds.length} segment(s)`);
  }
  return parts.join(' · ');
};
